import React, { FC, useState } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import SportCarousel from '../../UI-components/SportCarousel/SportCarousel'
import { useAppSelector } from '../../redux/hooks'
import { RootState } from '../../redux/store'
import { cleanEvent, EventData, eventInitialData } from '../../UI-components/Modals/CashierModal/CashierModal'
import { BRAND_KEY } from '../../core/const'
import { EventName } from '../../core/enums/moneyOperations'

interface SportPageProps {
	onSubmit: (eventName: string, data: any, close: boolean) => void
}

const SportPage: FC<SportPageProps> = ({ onSubmit }) => {
	const activeSport = useAppSelector((state: RootState) => state.sports.activeSport)
	const [eventData, setEventData] = useState<EventData>(eventInitialData)
	const [sportBetSent, setSportBetSent] = useState(false)
	const [activeBet, setActiveBet] = useState<{
		gameId: number
		selection: 'home' | 'draw' | 'away'
	} | null>(null)

	const { getTexts, selectedLanguage } = useTranslation(false)

	const sendEvent = (eventName: EventName, payload: any = {}, close: boolean = true) => {
		const data = cleanEvent(eventName, { ...eventData, ...payload, brand_key: BRAND_KEY })
		onSubmit(eventName, data, close)
	}

	const sportBet = (tempEventData: EventData) => {
		setSportBetSent(true)
		sendEvent(EventName.sport_bet_open, tempEventData, false)
	}

	const sportSettle = (win: boolean) => {
		setEventData((prev) => ({
			...prev,
			sport_win_amount: win ? prev.sport_bet_amount * prev.odds : 0,
			sport_bet_status: win,
		}))
		sendEvent(EventName.sport_bet_settled, eventData)
		setSportBetSent(false)
	}

	const handleBetClick = (type: 'home' | 'draw' | 'away', game: any, leagueName: string) => {
		const isDraw = type === 'draw'
		const selection = isDraw ? 'Draw' : game[type].name
		const odds = isDraw ? game.draw ?? 0 : game[type].odds ?? 0

		const updatedEventData: EventData = {
			...eventData,
			sport: activeSport?.sport_id,
			home_team: game.home.name,
			away_team: game.away.name,
			selection,
			odds,
			league: leagueName,
			sport_win_amount: eventData.sport_bet_amount * odds,
		}

		setEventData(updatedEventData)
		setSportBetSent(true)
		setActiveBet({
			gameId: game.id,
			selection: type,
		})

		sportBet(updatedEventData)
	}

	return (
		<div className="main-content sports-page">
			<SportCarousel />

			<div className="middle-wrapper">
				<div className="info-label">
					<div className="info-label__icon" />
					{getTexts('sports_betting_info')}
				</div>
				<div className="bet-amount-section">
					<div className="bet-amount-label">{getTexts('bet')}</div>
					<div className="bet-amount">
						<input
							type="number"
							placeholder="Bet amount"
							value={eventData.sport_bet_amount}
							onChange={(e) =>
								setEventData((prev) => ({
									...prev,
									sport_bet_amount: Number(e.target.value),
								}))
							}
						/>
					</div>
				</div>
			</div>

			<div className="leagues-lists">
				{activeSport?.leagues.map((league) => (
					<div key={league.league_name} className="league-block">
						<div className="league-block-header">
							<div className="league-block-title">{selectedLanguage === 'en' ? league.league_name : league.pt_name}</div>
							<div className="outcomes">
								<div className="league-block-header-home">{getTexts('home')}</div>
								{activeSport?.sport_id === 'soccer' && <div className="league-block-header-draw">{getTexts('draw')}</div>}
								<div className="league-block-header-away">{getTexts('away')}</div>
							</div>
						</div>

						{league.games.map((game: any) => (
							<div key={game.id} className="league-block-row">
								<div className="game-info-panel">
									<div className="league-block-time">{game.start_time}</div>
									<div className="league-block-teams">
										<div className="league-block-teams-home">
											<div className="team-name">{selectedLanguage === 'en' ? game.home.name : game.home.pt_name}</div>
											<div className="team-score">0</div>
										</div>
										<div className="league-block-teams-away">
											<div className="team-name">{selectedLanguage === 'en' ? game.away.name : game.away.pt_name}</div>
											<div className="team-score">0</div>
										</div>
									</div>
								</div>
								<div className="outcomes-values">
									{sportBetSent && activeBet?.gameId === game.id && activeBet?.selection === 'home' ? (
										<div className="active-bet-tile">
											<div className='bet-settle win' onClick={() => sportSettle(true)}>{getTexts('win')}</div>
											<div className='bet-settle loss' onClick={() => sportSettle(false)}>{getTexts('loss')}</div>
										</div>
									) : (
										<div className="league-block-odds-home" onClick={() => handleBetClick('home', game, league.league_name)}>
											{game.home.odds}
										</div>
									)}
									{activeSport?.sport_id === 'soccer' &&
										(sportBetSent && activeBet?.gameId === game.id && activeBet?.selection === 'draw' ? (
											<div className="active-bet-tile">
												<div className='bet-settle win' onClick={() => sportSettle(true)}>{getTexts('win')}</div>
												<div className='bet-settle loss' onClick={() => sportSettle(false)}>{getTexts('loss')}</div>
											</div>
										) : (
											<div className="league-block-odds-draw" onClick={() => handleBetClick('draw', game, league.league_name)}>
												{game.draw}
											</div>
										))}
									{sportBetSent && activeBet?.gameId === game.id && activeBet?.selection === 'away' ? (
										<div className="active-bet-tile">
											<div className='bet-settle win' onClick={() => sportSettle(true)}>{getTexts('win')}</div>
											<div className='bet-settle loss' onClick={() => sportSettle(false)}>{getTexts('loss')}</div>
										</div>
									) : (
										<div className="league-block-odds-away" onClick={() => handleBetClick('away', game, league.league_name)}>
											{game.away.odds}
										</div>
									)}
								</div>
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	)
}

export default SportPage
